import { create } from 'zustand';
import { esmsApi } from '../services/esms-api';
import type {
  EsmsOverview, EsmsTrendRow, EsmsGroupBy,
  EsmsBrandname, EsmsZaloOA, EsmsTopRecipient, EsmsPriceReference,
  EsmsTrendByTypeResponse, EsmsOtpStats,
} from '../types/esms';
import { format, startOfWeek, endOfWeek } from 'date-fns';

interface EsmsState {
  fromDate: string;
  toDate: string;
  trendGroupBy: EsmsGroupBy;

  overview: EsmsOverview | null;
  trend: EsmsTrendRow[];
  trendByType: EsmsTrendByTypeResponse | null;

  brandnames: EsmsBrandname[];
  zaloOA: EsmsZaloOA[];
  topRecipients: EsmsTopRecipient[];
  topRecipientsDisabled: boolean;
  priceReference: EsmsPriceReference | null;
  otpStats: EsmsOtpStats | null;
  otpStatsDisabled: boolean;

  reportFrom: string;
  reportTo: string;
  fetchedKey: string | null;

  isLoading: boolean;
  error: string | null;

  trendByTypeLoading: boolean;
  trendByTypeError: string | null;

  brandnamesLoading: boolean;
  brandnamesError: string | null;
  zaloOALoading: boolean;
  zaloOAError: string | null;
  topRecipientsLoading: boolean;
  topRecipientsError: string | null;
  priceReferenceLoading: boolean;
  priceReferenceError: string | null;
  otpStatsLoading: boolean;
  otpStatsError: string | null;

  setDateRange: (from: string, to: string) => void;
  setTrendGroupBy: (g: EsmsGroupBy) => void;
  fetchDashboardData: (force?: boolean) => Promise<void>;
  fetchTrend: (groupBy: EsmsGroupBy) => Promise<void>;
  fetchTrendByType: (groupBy: EsmsGroupBy) => Promise<void>;
  fetchAuxiliaryData: () => Promise<void>;
}

const now = new Date();
const thisMonday = startOfWeek(now, { weekStartsOn: 1 });
const lastMonday = new Date(thisMonday); lastMonday.setDate(thisMonday.getDate() - 7);
const lastSunday = endOfWeek(lastMonday, { weekStartsOn: 1 });
const defaultFrom = format(lastMonday, 'yyyy-MM-dd');
const defaultTo = format(lastSunday, 'yyyy-MM-dd');

function extractError(e: any, fallback = 'Request failed'): string {
  const parts: string[] = [];
  const status = e?.response?.status;
  const url = e?.config?.url;
  const method = e?.config?.method?.toUpperCase();
  const serverMsg = e?.response?.data?.error || e?.response?.data?.message;
  const serverStack = e?.response?.data?.stack;
  if (status) parts.push(`HTTP ${status}${method && url ? ` ${method} ${url}` : ''}`);
  if (serverMsg) parts.push(`Server: ${serverMsg}`);
  if (serverStack) parts.push(serverStack);
  if (!parts.length && e?.message) parts.push(e.message);
  if (!parts.length) parts.push(fallback);
  return parts.join('\n');
}

function daysBetween(from: string, to: string): number {
  const a = new Date(from + 'T00:00:00Z').getTime();
  const b = new Date(to + 'T00:00:00Z').getTime();
  return Math.floor((b - a) / 86400000) + 1;
}

export const useEsmsStore = create<EsmsState>((set, get) => ({
  fromDate: defaultFrom,
  toDate: defaultTo,
  trendGroupBy: 'day',

  overview: null,
  trend: [],
  trendByType: null,

  brandnames: [],
  zaloOA: [],
  topRecipients: [],
  topRecipientsDisabled: false,
  priceReference: null,
  otpStats: null,
  otpStatsDisabled: false,

  reportFrom: '',
  reportTo: '',
  fetchedKey: null,

  isLoading: false,
  error: null,

  trendByTypeLoading: false,
  trendByTypeError: null,

  brandnamesLoading: false,
  brandnamesError: null,
  zaloOALoading: false,
  zaloOAError: null,
  topRecipientsLoading: false,
  topRecipientsError: null,
  priceReferenceLoading: false,
  priceReferenceError: null,
  otpStatsLoading: false,
  otpStatsError: null,

  setDateRange: (from, to) => set({ fromDate: from, toDate: to }),
  setTrendGroupBy: (g) => set({ trendGroupBy: g }),

  fetchTrend: async (groupBy) => {
    const { fromDate, toDate } = get();
    try {
      const trend = await esmsApi.getTrend({ from: fromDate, to: toDate, groupBy });
      set({ trend, trendGroupBy: groupBy });
    } catch (e: any) {
      set({ error: extractError(e, 'Failed to load eSMS trend') });
    }
  },

  fetchTrendByType: async (groupBy) => {
    const { fromDate, toDate } = get();
    set({ trendByTypeLoading: true, trendByTypeError: null });
    try {
      const trendByType = await esmsApi.getTrendByType(fromDate, toDate, groupBy);
      set({ trendByType, trendByTypeLoading: false });
    } catch (e: any) {
      set({ trendByTypeError: extractError(e, 'Failed to load trend by type'), trendByTypeLoading: false });
    }
  },

  fetchAuxiliaryData: async () => {
    const { fromDate, toDate } = get();
    const rangeDays = daysBetween(fromDate, toDate);
    const recipientsAllowed = rangeDays <= 31;

    set({
      brandnamesLoading: true, brandnamesError: null,
      zaloOALoading: true, zaloOAError: null,
      topRecipientsLoading: recipientsAllowed, topRecipientsError: null,
      topRecipientsDisabled: !recipientsAllowed,
      priceReferenceLoading: true, priceReferenceError: null,
      otpStatsLoading: recipientsAllowed, otpStatsError: null,
      otpStatsDisabled: !recipientsAllowed,
    });

    const tasks: Promise<void>[] = [
      esmsApi.getBrandnames(fromDate, toDate)
        .then((r) => set({ brandnames: r.brandnames || [], brandnamesLoading: false }))
        .catch((e) => set({ brandnamesError: extractError(e, 'Failed to load brandnames'), brandnamesLoading: false })),
      esmsApi.getZaloOA()
        .then((r) => set({ zaloOA: r.oas || [], zaloOALoading: false }))
        .catch((e) => set({ zaloOAError: extractError(e, 'Failed to load Zalo OA'), zaloOALoading: false })),
      esmsApi.getPriceReference()
        .then((r) => set({ priceReference: r, priceReferenceLoading: false }))
        .catch((e) => set({ priceReferenceError: extractError(e, 'Failed to load price reference'), priceReferenceLoading: false })),
    ];

    if (recipientsAllowed) {
      tasks.push(
        esmsApi.getTopRecipients(fromDate, toDate, 20)
          .then((r) => set({ topRecipients: r.recipients || [], topRecipientsLoading: false }))
          .catch((e) => set({ topRecipientsError: extractError(e, 'Failed to load top recipients'), topRecipientsLoading: false })),
        esmsApi.getOtpStats(fromDate, toDate)
          .then((r) => set({ otpStats: r, otpStatsLoading: false }))
          .catch((e) => set({ otpStatsError: extractError(e, 'Failed to load OTP stats'), otpStatsLoading: false })),
      );
    } else {
      set({ topRecipients: [], otpStats: null });
    }

    await Promise.all(tasks);
  },

  fetchDashboardData: async (force = false) => {
    const { fromDate, toDate, fetchedKey, trendGroupBy, fetchAuxiliaryData } = get();
    const key = `${fromDate}|${toDate}|${trendGroupBy}`;
    if (!force && fetchedKey === key) return;
    set({ isLoading: true, error: null });
    try {
      const [overview, trend] = await Promise.all([
        esmsApi.getOverview({ from: fromDate, to: toDate }),
        esmsApi.getTrend({ from: fromDate, to: toDate, groupBy: trendGroupBy }),
      ]);
      set({
        overview,
        trend,
        reportFrom: fromDate,
        reportTo: toDate,
        fetchedKey: key,
        isLoading: false,
      });
      fetchAuxiliaryData();
    } catch (e: any) {
      set({ error: extractError(e, 'Failed to load eSMS dashboard'), isLoading: false });
    }
  },
}));
